import { HeadingText } from '@/components/atoms/HeadingText';
import { Textarea } from '@/components/atoms/Textarea';
import { useFormContext } from '@/components/forms/FormContext';
import { type RepairFormState, StepMap, steps } from '../../constants';
import { isValid, SystemIssuesStepActions } from './state';
import { Step } from '@/components/forms/Step';
import { handleNext } from './routing';
import { useValidateStep } from '@/components/forms/useValidateStep';
import { RepairFormActionMap } from '../../reducer';
import { WizardBoxCheckbox } from '@/components/WizardBox/WizardBoxCheckbox';
import cn from 'classnames';
import { ChangeEvent, useState } from 'react';
import { SpaceWow } from '@/components/SpaceWow';
import {
	IconLightning,
	IconThermometerChange,
	IconCircleQuestionMark,
	IconSoundWave,
	IconOdor,
	IconRefreshArrows,
} from '@/components/icons';

export interface SystemIssuesStepProps {
	testId?: string;
}

const SystemIssuesStep = (props: SystemIssuesStepProps) => {
	const { testId = 'SystemIssuesStep' } = props;
	const [showOther, setShowOther] = useState(false);
	const { state, dispatch } = useFormContext<
		StepMap,
		RepairFormState,
		RepairFormActionMap
	>();
	useValidateStep<StepMap, RepairFormState, RepairFormActionMap>(
		isValid,
		steps.SystemIssues,
	);

	const otherMessage =
		state.systemIssues
			.find((issue) => issue.startsWith('Other:'))
			?.slice(6)
			.trimStart() ?? '';

	const handleIssueSelectionChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { value, checked } = e.target;
		if (checked && value !== 'Something else') {
			dispatch({
				type: SystemIssuesStepActions.ADD_SYSTEM_ISSUE,
				payload: value,
			});
		} else {
			dispatch({
				type: SystemIssuesStepActions.REMOVE_SYSTEM_ISSUE,
				payload: value,
			});
		}

		if (value === 'Something else') {
			setShowOther(checked);

			if (checked) {
				dispatch({
					type: SystemIssuesStepActions.ADD_SYSTEM_ISSUE,
					payload: 'Other: ',
				});
			} else {
				const issueToRemove =
					state.systemIssues.find((issue) => issue.startsWith('Other:')) ?? '';
				dispatch({
					type: SystemIssuesStepActions.REMOVE_SYSTEM_ISSUE,
					payload: issueToRemove,
				});
			}
		}
	};

	const handleOtherIssueChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
		dispatch({
			type: SystemIssuesStepActions.UPDATE_OTHER_SYSTEM_ISSUE,
			payload: 'Other: ' + e.target.value,
		});
	};

	return (
		<div data-testid={testId}>
			<Step<StepMap, RepairFormState, RepairFormActionMap>
				name={steps.SystemIssues}
				onNext={handleNext}
			>
				<HeadingText
					tag="h1"
					size="h2"
					className="mb-3 mt-10 lg:mb-4 lg:mt-20"
					balanced
				>
					What&rsquo;s going on with your current system?
				</HeadingText>

				<p className="text-center text-xl tracking-tighter text-black lg:text-2xl">
					Choose as many as you like
				</p>

				<SpaceWow spacer_10 lg__spacer_14 />

				<div className="mx-auto flex max-w-5xl flex-wrap place-content-center gap-4 lg:gap-6">
					<WizardBoxCheckbox
						id="not-working"
						name="not-working"
						value="Not working"
						label="Not working"
						description="My system isn't heating or cooling"
						icon={
							<IconLightning className="h-auto w-full max-w-8 lg:max-w-10" />
						}
						onChange={handleIssueSelectionChange}
						checked={state.systemIssues.includes('Not working')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="poor-heating-cooling"
						name="poor-heating-cooling"
						value="Poor heating or cooling"
						label="Poor heating or cooling"
						description="Won't hold desired temperature"
						icon={
							<IconThermometerChange className="h-auto w-full max-w-8 lg:max-w-10" />
						}
						onChange={handleIssueSelectionChange}
						checked={state.systemIssues.includes('Poor heating or cooling')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="strange-noise"
						name="strange-noise"
						value="Strange noise or vibration"
						label="Strange noise or vibration"
						description="System is functioning oddly"
						icon={
							<IconSoundWave className="h-auto w-full max-w-8 lg:max-w-10" />
						}
						onChange={handleIssueSelectionChange}
						checked={state.systemIssues.includes('Strange noise or vibration')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="odd-smell"
						name="odd-smell"
						value="Musty or unusual smell"
						label="Musty or unusual smell"
						description="There's an odor that isn't normal"
						icon={<IconOdor className="h-auto w-full max-w-8 lg:max-w-10" />}
						onChange={handleIssueSelectionChange}
						checked={state.systemIssues.includes('Musty or unusual smell')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="time-to-replace"
						name="time-to-replace"
						value="Time to replace"
						label="Time to replace"
						description="It's time to replace my system"
						icon={
							<IconRefreshArrows className="h-auto w-full max-w-8 lg:max-w-10" />
						}
						onChange={handleIssueSelectionChange}
						checked={state.systemIssues.includes('Time to replace')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="something-else"
						name="something-else"
						value="Something else"
						label="Something else"
						description="My issue isn’t listed here"
						icon={
							<IconCircleQuestionMark className="h-auto w-full max-w-8 fill-black lg:max-w-10" />
						}
						onChange={handleIssueSelectionChange}
						checked={showOther}
						className="w-full lg:max-w-70"
					/>
				</div>
				<div className={cn({ hidden: !showOther })}>
					<SpaceWow spacer_08 lg__spacer_10 />
					<Textarea
						id="other"
						name="other"
						label="Tell us what’s going on"
						onChange={handleOtherIssueChange}
						value={otherMessage}
						fieldsetClassName="mx-auto lg:max-w-101"
					/>
				</div>
			</Step>
		</div>
	);
};

export default SystemIssuesStep;
